import React, { useEffect, useMemo } from "react";

import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import { Box, IconButton, Paper } from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, useNavigate } from "hooks";
import { useTaggerActions } from "store";

import {
  IMeasureGroupChild,
  IMeasureGroupOneInvestor,
  IProcessedTag,
  IRecommendationData,
  IRecommendationItem,
  RecommendationStatus,
  TaggerDocument
} from "store/types";
import EmptyDocumentView from "../../Tagger/Document/EmptyDocumentView";
import PDFReader from "./PDFReader";

interface Props {
  file: File | null;
  docId: TaggerDocument["id"];
  row: IRecommendationData;
  disabled?: boolean;
  onCancel?: () => void;
}

export const DocumentRead: React.FC<Props> = ({ docId, file, row, disabled = false, onCancel }) => {
  const { getDocumentPolygons } = useTaggerActions();
  const navigate = useNavigate();

  const { data: polygons, action: getPolygons, loading } = useActionHook(getDocumentPolygons);

  useEffect(() => {
    getPolygons(docId);
  }, []);

  const transformChildrenToTags = (
    children: (IMeasureGroupChild | IMeasureGroupOneInvestor | IRecommendationItem)[]
  ): IProcessedTag[] => {
    const tags: IProcessedTag[] = [];

    const processChild = (child: IMeasureGroupChild | IMeasureGroupOneInvestor | IRecommendationItem) => {
      // add current child as tag
      if ("polygon_id" in child && child.polygon_id) {
        tags.push({
          tag: { file_polygon_id: child.polygon_id || null },
          id: child.id || 0,
          measure: { id: child.measure?.id || 0, name: child.measure?.name || "" },
          value: child.value,
          is_draft: child.status !== RecommendationStatus.accepted,
          is_ai: true,
          is_splittable: false,
          series_id: null,
          document_id: docId,
          deal: { id: row.deal.id, title: row.deal.title },
          investor: child.investor ? { id: child.investor.id, name: child.investor.name } : null,
          round: null,
          currency: child.currency,
          accounting_date: child.date,
          effective_range_from: "",
          effective_range_to: "",
          description: "",
          split_id: 0,
          deleted_at: null,
          is_tagged: false,
          is_analysis: false,
          draft_measure_data_id: null,
          measure_data_id: null,
          needs_sync: false,
          account_id: 0,
          date: child.date,
          children: [],
          words: [{ rectId: child.polygon_id || "", text: child.value.toString() }],
          classification: child?.classification || "",
          is_child: false
        });
      }

      // Recursively process children
      if (child.children && child.children.length) {
        child.children.forEach(processChild);
      }
    };

    children.forEach(processChild);
    return tags;
  };

  const transformRowToTags: IProcessedTag[] = useMemo(() => {
    return row.children ? transformChildrenToTags(row.children) : [];
  }, [row, docId]);

  const navigateToTagger = () => {
    if (!row.file.section_id) return;
    onCancel?.();
    navigate({
      route: "tagger",
      search: { filesIds: [row.file.id], sections: [row.file.section_id], dealId: row.deal.id, aiData: true },
      setQueriesMethod: "replace"
    });
  };

  return (
    <AsyncContentMui isLoading={loading} boxProps={{ sx: { pt: 50 } }}>
      <Paper
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexGrow: "1",
          borderRadius: "8px",
          // maxWidth: "800px",
          position: "relative",
          ...(file && {
            padding: "10px"
          })
        }}
      >
        <IconButton sx={{ position: "absolute", right: "10px", top: "50px", zIndex: 1100 }} onClick={navigateToTagger}>
          <MaterialTooltip title="Open in Tagger" isShow>
            <CreateOutlinedIcon />
          </MaterialTooltip>
        </IconButton>
        {disabled && (
          <Box
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              bgcolor: "rgba(255, 255, 255, 0.5)",
              zIndex: 100
            }}
          />
        )}
        {!file && <EmptyDocumentView onLoadFiles={() => {}} />}
        {file && (
          <PDFReader
            docId={docId}
            file={file}
            error={null}
            polygons={polygons || null}
            tags={transformRowToTags || []}
            page={row.file.page || null}
          />
        )}
      </Paper>
    </AsyncContentMui>
  );
};
